'use client'

// CI retrigger
import { useState } from 'react'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Copy,
  Check,
  ExternalLink,
  Bell,
  CalendarClock,
  CheckCircle2,
  Video,
  Users,
  FileText,
  Mic,
  Image,
  Scissors,
  MessageSquare,
  Plus,
  Loader2,
} from 'lucide-react'
import type {
  RecordingDetail as RecordingDetailType,
  StageId,
  PrepChecklist,
  PostProduction,
} from '@/types'
import { formatFullDate, formatShortDate, formatTime, formatRelativeTime } from '@/lib/date'

export interface RecordingDetailProps {
  recording: RecordingDetailType
  onBack?: () => void
  onPrepToggle?: (field: keyof PrepChecklist) => void
  onSendReminder?: (guestId: string) => void
  onRemindAll?: () => void
  onReschedule?: () => void
  onMarkReady?: () => void
  onAddNote?: (note: string) => void
  onAddGuest?: () => void
  /** Set of guest IDs currently being sent reminders */
  sendingReminderIds?: Set<string>
  /** Set of guest IDs who were just sent reminders (for success feedback) */
  reminderSentIds?: Set<string>
  /** Whether "Remind All" is in progress */
  isRemindingAll?: boolean
  /** Map of guest ID to minutes remaining on cooldown */
  guestCooldowns?: Record<string, number>
  /** Error message to display */
  reminderError?: string | null
  /** Callback to dismiss error */
  onDismissError?: () => void
  /** Whether all guests are already marked ready to record */
  isAlreadyReady?: boolean
  /** Whether the mark-ready mutation is in progress */
  isMarkingReady?: boolean
}

const stages: { id: StageId; label: string }[] = [
  { id: 'invited', label: 'Invited' },
  { id: 'responded', label: 'Responded' },
  { id: 'prep', label: 'Prep' },
  { id: 'recorded', label: 'Recorded' },
  { id: 'published', label: 'Published' },
]

function getStageIndex(stage: StageId): number {
  return stages.findIndex((s) => s.id === stage)
}

function getPostProductionStatus(
  status: 'not-started' | 'in-progress' | 'complete'
): { label: string; color: string; bg: string } {
  const configs = {
    'not-started': {
      label: 'Not Started',
      color: 'text-zinc-500 dark:text-zinc-400',
      bg: 'bg-zinc-100 dark:bg-zinc-800',
    },
    'in-progress': {
      label: 'In Progress',
      color: 'text-[#FF6F00] dark:text-[#FF8F33]',
      bg: 'bg-[#FF6F00]/10 dark:bg-[#FF6F00]/20',
    },
    complete: {
      label: 'Complete',
      color: 'text-[#0d9488] dark:text-[#60E1E0]',
      bg: 'bg-[#60E1E0]/10 dark:bg-[#60E1E0]/20',
    },
  }
  return configs[status]
}

export function RecordingDetail({
  recording,
  onBack,
  onPrepToggle,
  onSendReminder,
  onRemindAll,
  onReschedule,
  onMarkReady,
  onAddNote,
  onAddGuest,
  sendingReminderIds = new Set(),
  reminderSentIds = new Set(),
  isRemindingAll,
  guestCooldowns = {},
  reminderError,
  onDismissError,
  isAlreadyReady = false,
  isMarkingReady = false,
}: RecordingDetailProps) {
  const [copiedLink, setCopiedLink] = useState(false)
  const [newNote, setNewNote] = useState('')

  const currentStageIndex = getStageIndex(recording.episode.stage)

  const handleCopyLink = async () => {
    if (recording.episode.meetingLink) {
      await navigator.clipboard.writeText(recording.episode.meetingLink)
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    }
  }

  const handleAddNote = () => {
    if (newNote.trim() && onAddNote) {
      onAddNote(newNote.trim())
      setNewNote('')
    }
  }

  const prepItems: { key: keyof PrepChecklist; label: string }[] = [
    { key: 'researchComplete', label: 'Research complete' },
    { key: 'questionsReady', label: 'Questions prepared' },
    { key: 'guestBriefed', label: 'Guest briefed' },
    { key: 'equipmentTested', label: 'Equipment tested' },
  ]

  const postProductionItems: { key: keyof PostProduction; label: string; icon: React.ReactNode }[] = [
    { key: 'editing', label: 'Editing', icon: <Scissors className="w-4 h-4" /> },
    { key: 'transcription', label: 'Transcription', icon: <FileText className="w-4 h-4" /> },
    { key: 'showNotes', label: 'Show Notes', icon: <MessageSquare className="w-4 h-4" /> },
    { key: 'artwork', label: 'Artwork', icon: <Image className="w-4 h-4" /> },
  ]

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to recordings</span>
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-3 py-1 rounded-full bg-[#FF6F00]/10 text-[#FF6F00] font-semibold text-sm">
            Episode {recording.episode.number}
          </span>
          {recording.episode.duration && (
            <span className="flex items-center gap-1 text-sm text-zinc-500 dark:text-zinc-400">
              <Clock className="w-4 h-4" />
              {recording.episode.duration}
            </span>
          )}
        </div>
        <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
          {recording.episode.title}
        </h1>
        {recording.episode.description && (
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">{recording.episode.description}</p>
        )}
      </div>

      {/* Stage Progress Indicator */}
      <div className="mb-8 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-4">
          Progress
        </h2>
        <div className="flex items-center justify-between">
          {stages.map((stage, index) => {
            const isComplete = index < currentStageIndex
            const isCurrent = index === currentStageIndex
            const _isUpcoming = index > currentStageIndex

            return (
              <div key={stage.id} className="flex items-center flex-1">
                {/* Stage Node */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                      isComplete
                        ? 'bg-[#60E1E0] border-[#60E1E0] text-white'
                        : isCurrent
                        ? 'bg-[#FF6F00] border-[#FF6F00] text-white'
                        : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-400'
                    }`}
                  >
                    {isComplete ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      isCurrent
                        ? 'text-[#FF6F00]'
                        : isComplete
                        ? 'text-[#0d9488] dark:text-[#60E1E0]'
                        : 'text-zinc-400 dark:text-zinc-500'
                    }`}
                  >
                    {stage.label}
                  </span>
                </div>

                {/* Connector Line */}
                {index < stages.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      index < currentStageIndex
                        ? 'bg-[#60E1E0]'
                        : 'bg-zinc-200 dark:bg-zinc-700'
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recording Details Card */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <Video className="w-5 h-5 text-[#6369D1]" />
                Recording Details
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-zinc-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Recording Date</p>
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">
                      {formatFullDate(recording.episode.recordingDate)}
                    </p>
                  </div>
                </div>
                {recording.episode.recordingTime && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-zinc-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">Time</p>
                      <p className="font-medium text-zinc-900 dark:text-zinc-100">
                        {formatTime(recording.episode.recordingTime)}
                      </p>
                    </div>
                  </div>
                )}
                {recording.episode.platform && (
                  <div className="flex items-start gap-3">
                    <Mic className="w-5 h-5 text-zinc-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">Platform</p>
                      <p className="font-medium text-zinc-900 dark:text-zinc-100">
                        {recording.episode.platform}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#60E1E0] mt-0.5" />
                  <div>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Air Date</p>
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">
                      {formatFullDate(recording.episode.airDate)}
                    </p>
                  </div>
                </div>
              </div>

              {recording.episode.meetingLink && (
                <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-2">Meeting Link</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-800 text-sm text-zinc-700 dark:text-zinc-300 truncate">
                      {recording.episode.meetingLink}
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="p-2 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      title="Copy link"
                    >
                      {copiedLink ? (
                        <Check className="w-4 h-4 text-[#60E1E0]" />
                      ) : (
                        <Copy className="w-4 h-4 text-zinc-500" />
                      )}
                    </button>
                    <a
                      href={recording.episode.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      title="Open link"
                    >
                      <ExternalLink className="w-4 h-4 text-zinc-500" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Prep Checklist */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#60E1E0]" />
                Prep Checklist
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {prepItems.map((item) => {
                  const isChecked = recording.prep[item.key]
                  return (
                    <button
                      key={item.key}
                      onClick={() => onPrepToggle?.(item.key)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors text-left"
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-[#60E1E0] border-[#60E1E0]'
                            : 'border-zinc-300 dark:border-zinc-600'
                        }`}
                      >
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span
                        className={`flex-1 ${
                          isChecked
                            ? 'text-zinc-500 dark:text-zinc-400 line-through'
                            : 'text-zinc-900 dark:text-zinc-100'
                        }`}
                      >
                        {item.label}
                      </span>
                    </button>
                  )
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500 dark:text-zinc-400">Progress</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">
                    {Object.values(recording.prep).filter(Boolean).length} / {prepItems.length}{' '}
                    complete
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-[#60E1E0] transition-all"
                    style={{
                      width: `${
                        (Object.values(recording.prep).filter(Boolean).length / prepItems.length) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Guests Section */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FF6F00]" />
                Guests ({recording.guests.length})
              </h2>
              {onAddGuest && (
                <button
                  onClick={onAddGuest}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#FF6F00] hover:bg-[#FF6F00]/10 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Guest
                </button>
              )}
            </div>
            <div className="p-6">
              {/* Error Alert */}
              {reminderError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center justify-between">
                  <p className="text-sm text-red-600 dark:text-red-400">{reminderError}</p>
                  <button
                    onClick={onDismissError}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                  >
                    <span className="sr-only">Dismiss</span>
                    ×
                  </button>
                </div>
              )}
              <div className="space-y-4">
                {recording.guests.map((guest) => {
                  const isSending = sendingReminderIds.has(guest.id)
                  const wasSent = reminderSentIds.has(guest.id)
                  const cooldownMinutes = guestCooldowns[guest.id]
                  const isOnCooldown = cooldownMinutes !== undefined && cooldownMinutes > 0

                  return (
                    <div
                      key={guest.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={guest.avatarUrl}
                          alt={guest.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <h4 className="font-medium text-zinc-900 dark:text-zinc-100">
                            {guest.name}
                          </h4>
                          <p className="text-sm text-zinc-500 dark:text-zinc-400">
                            {guest.title}
                            {guest.company && ` at ${guest.company}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onSendReminder?.(guest.id)}
                        disabled={isSending || isOnCooldown}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                          wasSent
                            ? 'border-green-500 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                            : isOnCooldown
                            ? 'border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800 cursor-not-allowed'
                            : 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                        } ${isSending ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isSending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : wasSent ? (
                          <Check className="w-4 h-4" />
                        ) : isOnCooldown ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                        <span>
                          {wasSent
                            ? 'Sent!'
                            : isSending
                            ? 'Sending...'
                            : isOnCooldown
                            ? `Wait ${cooldownMinutes} min`
                            : 'Send Reminder'}
                        </span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Post-Production Section */}
          {recording.postProduction && (
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
                <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                  <Scissors className="w-5 h-5 text-[#6369D1]" />
                  Post-Production
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {postProductionItems.map((item) => {
                    const status = getPostProductionStatus(recording.postProduction![item.key])
                    return (
                      <div
                        key={item.key}
                        className="flex items-center justify-between p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-zinc-400">{item.icon}</div>
                          <span className="font-medium text-zinc-900 dark:text-zinc-100">
                            {item.label}
                          </span>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}
                        >
                          {status.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100">Activity Timeline</h2>
            </div>
            <div className="p-6">
              {recording.activity.length > 0 ? (
                <div className="space-y-4">
                  {recording.activity.map((item, index) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="relative">
                        <div className="w-2 h-2 rounded-full bg-[#6369D1] mt-2" />
                        {index < recording.activity.length - 1 && (
                          <div className="absolute top-4 left-0.5 w-0.5 h-full bg-zinc-200 dark:bg-zinc-700" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="text-sm text-zinc-900 dark:text-zinc-100">{item.action}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                          {formatRelativeTime(item.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">
                  No activity recorded yet
                </p>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <FileText className="w-5 h-5 text-zinc-400" />
                Notes
              </h2>
            </div>
            <div className="p-6">
              {recording.notes && (
                <div className="mb-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
                    {recording.notes}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-4 py-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#FF6F00]/50 focus:border-[#FF6F00]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddNote()
                    }
                  }}
                />
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className="px-4 py-2 rounded-lg bg-[#FF6F00] text-white font-medium hover:bg-[#E56300] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Quick Actions */}
        <div className="space-y-6">
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden sticky top-6">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="font-semibold text-zinc-900 dark:text-zinc-100">Quick Actions</h2>
            </div>
            <div className="p-4 space-y-3">
              <button
                onClick={onRemindAll}
                disabled={isRemindingAll}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#FF6F00] text-white font-medium hover:bg-[#E56300] transition-colors ${isRemindingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isRemindingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                <span>{isRemindingAll ? 'Sending...' : 'Remind All Guests'}</span>
              </button>
              <button
                onClick={onReschedule}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                <CalendarClock className="w-4 h-4" />
                <span>Reschedule</span>
              </button>
              <button
                onClick={onMarkReady}
                disabled={isAlreadyReady || isMarkingReady}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                  isAlreadyReady
                    ? 'border-[#60E1E0] bg-[#60E1E0]/10 text-[#0d9488] dark:text-[#60E1E0] cursor-default'
                    : isMarkingReady
                    ? 'border-[#60E1E0] text-[#0d9488] dark:text-[#60E1E0] opacity-70 cursor-not-allowed'
                    : 'border-[#60E1E0] text-[#0d9488] dark:text-[#60E1E0] hover:bg-[#60E1E0]/10'
                }`}
              >
                {isMarkingReady ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                <span>
                  {isAlreadyReady
                    ? 'Ready to Record'
                    : isMarkingReady
                    ? 'Marking Ready...'
                    : 'Mark Ready to Record'}
                </span>
              </button>
            </div>

            {/* Air Date Countdown */}
            <div className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500 dark:text-zinc-400">Air Date</span>
                <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {formatShortDate(recording.episode.airDate)}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#60E1E0]" />
                <span className="text-sm text-[#0d9488] dark:text-[#60E1E0] font-medium">
                  {Math.ceil(
                    (new Date(recording.episode.airDate).getTime() - new Date().getTime()) /
                      (1000 * 60 * 60 * 24)
                  )}{' '}
                  days until air
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
